import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';

import { getBadgeLowPower, sendBadgeLowPowerEmail, sendBadgeLowPowerText } from '../api';
import { UserTypes } from '../constants';
import { T, FONT_MONO, INDICATOR, INDICATOR_TEXT } from '../constants/tokens';

const useStyles = makeStyles({
  button: {
    flex: '1 1 auto',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 500,
    height: 36,
    padding: '0 14px',
    borderRadius: 6,
    border: '1px solid ' + T.hairline,
    background: T.elevated,
    color: T.ink,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover:not(:disabled)': { background: T.surfaceSoft },
    '&:disabled': { color: T.mutedSoft, cursor: 'default' },
    '&:focus-visible': { outline: '2px solid ' + T.focus, outlineOffset: 2 }
  }
});

/** "9:41 AM" today, otherwise "Sep 24, 9:41 AM". */
const when = (iso) => {
  const t = DateTime.fromISO(iso).toLocal();
  return t.hasSame(DateTime.now(), 'day')
    ? t.toLocaleString(DateTime.TIME_SIMPLE)
    : t.toLocaleString({ month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** One log entry as a sentence. */
const describe = (entry) => {
  if (entry.channel === 'email') {
    if (entry.error) return 'Email not sent: ' + entry.error;
    return (entry.automatic ? 'Automatic email sent to ' : 'Email resent to ') + (entry.to ?? 'the badge holder') +
      (entry.level != null ? ' at ' + Math.floor(entry.level) + '%' : '') + '.';
  }
  if (entry.error) return 'Badge text not sent: ' + entry.error;
  const sent = entry.automatic ? 'Charge reminder shown on the badge' : 'Charge reminder sent to the badge';
  // Quote what the badge was sent, as it was then; older entries lack it.
  return entry.text ? sent + ': “' + entry.text + '”' : sent + '.';
};

/**
 * The design's "Low power notifications" block, for a badge below the low
 * power threshold: what has been sent to its holder and when, with "Send text
 * to badge" and "Resend email".
 *
 * Whether the badge counts as low, and the threshold, come from the server,
 * so this follows the school's setting. Sending is for Admin, IT and Super;
 * the server enforces it too.
 */
function LowPowerNotifications({ serialNumber, canSend }) {
  const classes = useStyles();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  const load = useCallback(() => {
    getBadgeLowPower(serialNumber)
      .then((result) => {
        if (mounted.current) setState(result);
      })
      .catch(() => {
        // Nothing to show without it; the rest of the panel stands.
      });
  }, [serialNumber]);

  useEffect(() => {
    mounted.current = true;
    setState(null);
    setError(null);
    load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  if (state == null || !state.isLow) return null;

  const send = (kind) => {
    setBusy(kind);
    setError(null);
    (kind === 'email' ? sendBadgeLowPowerEmail : sendBadgeLowPowerText)(serialNumber)
      .then((result) => {
        if (mounted.current) setState(result);
      })
      .catch((reason) => {
        if (!mounted.current) return;
        setError(typeof reason === 'string' ? reason : 'Could not send.');
        load();
      })
      .finally(() => {
        if (mounted.current) setBusy(null);
      });
  };

  const textSent = state.log.some((entry) => entry.channel === 'text' && !entry.error);

  return (
    <div style={{ margin: '16px 0', padding: 16, borderRadius: 6, border: '1px solid ' + T.hairline, background: T.surfaceSoft }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: 9999, flex: 'none', background: INDICATOR.warning }} />
        <span style={{ fontFamily: FONT_MONO, fontSize: 12, fontWeight: 500, lineHeight: '16px', textTransform: 'uppercase', color: INDICATOR_TEXT.warning }}>
          Low power notifications · {state.level == null ? '—' : Math.floor(state.level) + '%'}
        </span>
      </div>

      {state.log.length === 0 &&
        <div style={{ fontSize: 13, lineHeight: '18px', color: T.body }}>
          Nothing sent yet.
          {!state.emailEnabled && !state.textEnabled && ' Automatic notices are switched off in Platform settings.'}
        </div>}

      {state.log.map((entry) => (
        <div key={entry.id} style={{ display: 'grid', gridTemplateColumns: '72px minmax(0, 1fr)', gap: 8, padding: '4px 0' }}>
          <span style={{ fontFamily: FONT_MONO, fontSize: 12, lineHeight: '18px', color: T.muted, whiteSpace: 'nowrap' }}>{when(entry.timestamp)}</span>
          <span style={{ fontSize: 13, lineHeight: '18px', color: entry.error ? INDICATOR_TEXT.error : T.ink }}>{describe(entry)}</span>
        </div>
      ))}

      {canSend &&
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          <button type='button' className={classes.button} disabled={busy != null} onClick={() => send('text')}>
            {busy === 'text' ? 'Sending…' : (textSent ? 'Send text again' : 'Send text to badge')}
          </button>
          <button type='button' className={classes.button} disabled={busy != null} onClick={() => send('email')}>
            {busy === 'email' ? 'Sending…' : 'Resend email'}
          </button>
        </div>}

      {error && <div style={{ fontSize: 13, lineHeight: '18px', color: INDICATOR_TEXT.error, marginTop: 8 }}>{error}</div>}
    </div>
  );
}

LowPowerNotifications.propTypes = {
  serialNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  canSend: PropTypes.bool
};

const mapStateToProps = (state) => ({
  // By name: a rehydrated user type is restored to the constant, but compare
  // by name anyway so this never depends on reference identity.
  canSend: ['Admin', 'IT', 'Super'].includes(state.auth.userType?.name) ||
    [UserTypes.ADMIN, UserTypes.IT, UserTypes.SUPER].includes(state.auth.userType)
});

export default connect(mapStateToProps)(LowPowerNotifications);
